import {
  Office,
  Opportunity,
  OpportunityEntryKind,
  OpportunityPipelineStatus,
  OpportunityStage,
  Project,
  User,
} from '../types';
import { ROLE } from './roles';

export const OPPORTUNITY_STAGE_LABELS: Record<OpportunityStage, string> = {
  lead: 'Lead',
  qualified: 'Qualified',
  bid_preparation: 'Bid Prep',
  submitted: 'Submitted',
  negotiation: 'Negotiation',
  won: 'Won',
  lost: 'Lost',
  on_hold: 'On Hold',
};

export const OPPORTUNITY_STATUS_LABELS: Record<OpportunityPipelineStatus, string> = {
  new: 'New',
  qualified: 'Qualified',
  opportunity: 'Opportunity',
  bidding: 'Bidding',
  negotiation: 'Negotiation',
  won: 'Won',
  lost: 'Lost',
  other: 'Other',
};

export const OPPORTUNITY_STATUS_DESCRIPTIONS: Partial<Record<OpportunityPipelineStatus, string>> = {
  new: 'A new lead or request has been received and recorded.',
  qualified: 'The lead has been assessed and confirmed as a credible opportunity.',
  opportunity: 'A concrete project or business need has been identified.',
  bidding: 'A formal bid has been submitted to the client.',
  negotiation: 'Terms are being discussed with the client to reach an agreement.',
  other: 'The lead ended without being won or lost.',
};

export const OPPORTUNITY_ENTRY_KIND_LABELS: Record<OpportunityEntryKind, string> = {
  lead: 'Lead',
  opportunity: 'Opportunity',
};

export const OPPORTUNITY_PIPELINE_ORDER: OpportunityPipelineStatus[] = [
  'new',
  'qualified',
  'opportunity',
  'bidding',
  'negotiation',
];

export type OpportunityStatusFilter = OpportunityPipelineStatus | 'all';

export const OPPORTUNITY_STATUS_FILTERS: OpportunityStatusFilter[] = [
  'all',
  'new',
  'qualified',
  'opportunity',
  'bidding',
  'negotiation',
  'won',
  'lost',
  'other',
];

export const STATUS_PROBABILITIES: Record<OpportunityPipelineStatus, number> = {
  new: 10,
  qualified: 25,
  opportunity: 50,
  bidding: 70,
  negotiation: 85,
  won: 100,
  lost: 0,
  other: 0,
};

export const HOLD_PROJECT_TYPE = 'Hold';

export const SALES_PROJECT_TYPES = [
  'Oil & Gas',
  'Renewables',
  'Port & Infrastructures',
  'EPCI',
  'Studies (monodiscipline)',
  'Concept, FEED',
  'Inspection & Maintenance',
  HOLD_PROJECT_TYPE,
] as const;

export type SalesProjectType = (typeof SALES_PROJECT_TYPES)[number];

export type OpportunityFieldKey =
  | 'title'
  | 'client_id'
  | 'contact_name'
  | 'email'
  | 'source'
  | 'commercial_owner_id'
  | 'office_id'
  | 'notes'
  | 'project_type'
  | 'project_identified'
  | 'project_location'
  | 'expected_decision_date'
  | 'estimated_value'
  | 'opportunity_value'
  | 'expected_closing_date'
  | 'proposal_amount'
  | 'proposal_date'
  | 'next_action_date'
  | 'lost_reason'
  | 'reason'
  | 'final_value'
  | 'closing_date';

export type OpportunityStageConfig = {
  label: string;
  fields: OpportunityFieldKey[];
  requiredFields: OpportunityFieldKey[];
};

export const OPPORTUNITY_STAGE_CONFIG: Record<OpportunityPipelineStatus, OpportunityStageConfig> = {
  new: {
    label: 'New',
    fields: [
      'title',
      'client_id',
      'contact_name',
      'email',
      'source',
      'commercial_owner_id',
      'office_id',
      'notes',
    ],
    requiredFields: ['title', 'commercial_owner_id', 'office_id'],
  },
  qualified: {
    label: 'Qualified',
    fields: [
      'project_type',
      'project_identified',
      'project_location',
      'expected_decision_date',
      'estimated_value',
      'notes',
    ],
    requiredFields: [],
  },
  opportunity: {
    label: 'Opportunity',
    fields: ['opportunity_value', 'expected_closing_date', 'notes'],
    requiredFields: [],
  },
  bidding: {
    label: 'Bidding',
    fields: ['proposal_amount', 'proposal_date', 'notes'],
    requiredFields: [],
  },
  negotiation: {
    label: 'Negotiation',
    fields: ['expected_closing_date', 'next_action_date', 'notes'],
    requiredFields: ['expected_closing_date', 'next_action_date'],
  },
  won: {
    label: 'Won',
    fields: ['final_value', 'closing_date', 'notes'],
    requiredFields: [],
  },
  lost: {
    label: 'Lost',
    fields: ['lost_reason', 'notes'],
    requiredFields: ['lost_reason'],
  },
  other: {
    label: 'Other',
    fields: ['reason', 'next_action_date', 'notes'],
    requiredFields: ['reason'],
  },
};

export const PROJECT_IDENTIFIED_OPTIONS: Array<{ value: 'yes' | 'no' | 'to_be_defined'; label: string }> = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'to_be_defined', label: 'To be defined' },
];

export const getOpportunityStatusLabel = (status: string) =>
  OPPORTUNITY_STATUS_LABELS[status as OpportunityPipelineStatus]
  || status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const getOpportunityStatusFilterLabel = (filter: OpportunityStatusFilter) => {
  if (filter === 'all') return 'All';
  return OPPORTUNITY_STATUS_LABELS[filter];
};

export const isPipelineStatus = (
  status: OpportunityPipelineStatus | string,
): status is OpportunityPipelineStatus =>
  status in OPPORTUNITY_STATUS_LABELS;

export const isTerminalOpportunityStatus = (status: OpportunityPipelineStatus | string) =>
  status === 'won' || status === 'lost' || status === 'other';

export const getNextPipelineStatus = (
  status: OpportunityPipelineStatus,
): OpportunityPipelineStatus | null => {
  if (status === 'negotiation') return null;
  const index = OPPORTUNITY_PIPELINE_ORDER.indexOf(status);
  if (index < 0 || index >= OPPORTUNITY_PIPELINE_ORDER.length - 1) return null;
  return OPPORTUNITY_PIPELINE_ORDER[index + 1];
};

export const getStagesUpToStatus = (status: OpportunityPipelineStatus): OpportunityPipelineStatus[] => {
  if (status === 'won' || status === 'lost' || status === 'other') {
    return [...OPPORTUNITY_PIPELINE_ORDER, status];
  }
  const index = OPPORTUNITY_PIPELINE_ORDER.indexOf(status);
  if (index < 0) return ['new'];
  return OPPORTUNITY_PIPELINE_ORDER.slice(0, index + 1);
};

export const getCumulativeRequiredFields = (status: OpportunityPipelineStatus): OpportunityFieldKey[] => {
  const required = new Set<OpportunityFieldKey>();
  if (status === 'won' || status === 'lost' || status === 'other') {
    for (const stage of OPPORTUNITY_PIPELINE_ORDER) {
      OPPORTUNITY_STAGE_CONFIG[stage].requiredFields.forEach((field) => required.add(field));
    }
    OPPORTUNITY_STAGE_CONFIG[status].requiredFields.forEach((field) => required.add(field));
    if (status === 'other') {
      required.delete('next_action_date');
    }
    return Array.from(required);
  }

  for (const stage of OPPORTUNITY_PIPELINE_ORDER) {
    OPPORTUNITY_STAGE_CONFIG[stage].requiredFields.forEach((field) => required.add(field));
    if (stage === status) break;
  }
  return Array.from(required);
};

export const getMissingRequiredFields = (
  status: OpportunityPipelineStatus,
  values: Partial<Record<OpportunityFieldKey, unknown>>,
): OpportunityFieldKey[] =>
  getCumulativeRequiredFields(status).filter((field) => {
    const value = values[field];
    if (field === 'commercial_owner_id' || field === 'office_id' || field === 'client_id') {
      return value === '' || value === null || value === undefined;
    }
    if (typeof value === 'string') return !value.trim();
    return value === null || value === undefined;
  });

export const getDefaultProbability = (status: OpportunityPipelineStatus) =>
  STATUS_PROBABILITIES[status] ?? 10;

export const getOpportunityValueAmount = (opportunity: Opportunity): number =>
  opportunity.final_value
  ?? opportunity.proposal_amount
  ?? opportunity.opportunity_value
  ?? opportunity.estimated_value
  ?? 0;

export const formatMoney = (value?: number) =>
  value ? new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value) : '-';

export const formatHours = (value?: number) =>
  value ? new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value) : '-';

export const toDate = (value?: string) => (value ? new Date(`${value}T00:00:00`) : null);

export const daysUntil = (value?: string) => {
  const date = toDate(value);
  if (!date) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((date.getTime() - today.getTime()) / 86400000);
};

export const isActiveProject = (project: Project) => {
  const status = (project.status || '').toLowerCase();
  if (status.includes('cancel') || status.includes('complete') || status.includes('close')) {
    return false;
  }

  const plannedEnd = toDate(project.project_revised_end_date || project.project_planned_end_date);
  if (!plannedEnd) return true;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return plannedEnd >= today;
};

export const getOfficeName = (officeName?: string, officeCode?: string, officeCountry?: string) =>
  officeName || officeCode || officeCountry || 'Unassigned';

export const getOfficeLabel = (office?: Office) =>
  office?.name || office?.code || office?.country || 'Unassigned';

export const getOfficeLabelById = (officeId: number | undefined, officesById: Map<number, Office>) =>
  officeId ? getOfficeLabel(officesById.get(officeId)) : 'Unassigned';

export const getLoadRisk = (ratio: number): 'low' | 'medium' | 'high' => {
  if (ratio >= 1) return 'high';
  if (ratio >= 0.75) return 'medium';
  return 'low';
};

export const getPmPressureRisk = (score: number): 'low' | 'medium' | 'high' => {
  if (score >= 5) return 'high';
  if (score >= 3) return 'medium';
  return 'low';
};

export const getRiskColor = (risk: 'low' | 'medium' | 'high') => {
  switch (risk) {
    case 'high':
      return 'error';
    case 'medium':
      return 'warning';
    default:
      return 'success';
  }
};

export const getWeightedOpportunityHours = (opportunity: Opportunity) =>
  (opportunity.estimated_manhours || 0) * (opportunity.probability_to_win || 0) / 100;

export const getWeightedOpportunityValue = (opportunity: Opportunity) =>
  getOpportunityValueAmount(opportunity) * (opportunity.probability_to_win || 0) / 100;

export const getUpcomingOpportunities = (opportunities: Opportunity[], daysWindow: number) =>
  opportunities.filter((opportunity) => {
    if (isTerminalOpportunityStatus(opportunity.status)) return false;
    const delta = daysUntil(opportunity.expected_closing_date);
    return delta !== null && delta >= 0 && delta <= daysWindow;
  });

const COMMERCIAL_OWNER_ROLES: readonly string[] = [
  ROLE.PM,
  ROLE.DIRECTOR,
  ROLE.ADMIN,
  ROLE.SALES,
];

export const getActiveSalesUsers = (users: User[]) =>
  users.filter((user) => COMMERCIAL_OWNER_ROLES.some((role) => user.roles.includes(role)));
